<template>
  <div :class="['input_tags', {'input_tags--error': error !== null}]">
    <div :class="{required: required}">
      <label class="label">
        {{ label }}
      </label>
    </div>
    <div
      ref="inputTagsContainer"
      :class="['tag__container', 'd-flex', 'flex-wrap']"
    >
      <InputTag
        v-for="(tag, index) in tags"
        :key="index"
        :tag="tag[keyValue]"
        :locked="'locked' in tag"
        :on-remove="() => onRemoval(tag)"
        :repeated="repeated"
      />
      <div
        ref="newTagInputContainer"
        class="tag__input mt-1"
      >
        <input
          ref="newTagInput"
          v-model="newTag"
          :class="{error: repeated !== null}"
          :tabindex="tabIndex"
          @keydown="addTag"
          @focus="focus"
          @blur="blur"
        >
      </div>
      <div
        v-show="showDropdown"
        ref="dropdown"
        class="dropdown"
      >
        <div class="items d-flex flex-column required">
          <div v-if="filteredAvailableTags.length">
            <div
              v-for="tag in filteredAvailableTags"
              :key="tag.id"
              :class="['item', {selected: selectedItem === tag.id}]"
              @click="() => onItemClick(tag.id)"
              @mouseover="() => onItemMouseover(tag.id)"
            >
              {{ tag.name }}
            </div>
          </div>
          <div
            v-else
            class="empty"
          >
            No {{ label.toLowerCase() }} found
          </div>
        </div>
      </div>
    </div>
    <div :class="['d-flex', error === null ? 'justify-content-end' : 'justify-content-between']">
      <div v-show="error !== null">
        {{ error }}
      </div>
      <div
        v-show="showCounter"
        class="counter"
      >
        {{ count }}
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import InputTag from './InputTag'

export default {
  components: {
    InputTag
  },
  model: {
    prop: 'tags',
    event: 'tagsupdated'
  },
  props: {
    label: {
      type: String,
      required: false,
      default: 'Tags'
    },
    required: {
      type: Boolean,
      required: false,
      default: false
    },
    tags: Array,
    showCounter: Boolean,
    setError: {
      type: Function,
      required: false,
      default: () => {}
    },
    listOnly: {
      type: Boolean,
      required: false,
      default: false
    },
    availableTags: {
      type: Array,
      required: false,
      default: () => []
    },
    keyValue: {
      type: String,
      required: false,
      default: 'name'
    },
    tabIndex: {
      type: Number,
      required: false,
      default: 0
    },
    minTags: {
      type: Number,
      required: false,
      default: 0
    },
    maxTags: {
      type: Number,
      required: false,
      default: 2000
    },
    minTagLength: {
      type: Number,
      required: false,
      default: 3
    },
    maxTagLength: {
      type: Number,
      required: false,
      default: 25
    },
    parentError: {
      type: String,
      required: false
    }
  },
  data () {
    return {
      newTag: '',
      newTagId: -1,
      repeated: null,
      error: null,
      emptied: 0,
      showDropdown: false,
      selectedItem: -1,
      selectedItemByMouseover: false
    }
  },
  computed: {
    count: function () {
      return this.tags.length
    },
    filteredAvailableTags: function () {
      return this.availableTags.filter(tag => {
        return this.tags.findIndex(t => t.id === tag.id) === -1 && tag.name.toLowerCase().indexOf(this.newTag.toLowerCase()) > -1
      })
    }
  },
  watch: {
    newTag: function (newVal, oldVal) {
      const diff = oldVal.length - newVal.length
      let backspaceValue = 1
      if (diff > 2) {
        backspaceValue = 0
      }
      this.emptied = oldVal.length > 0 && newVal.length === 0 ? backspaceValue : -1
    },
    selectedItem: function () {
      Vue.nextTick(() => {
        if (this.selectedItemByMouseover) return
        const { dropdown } = this.$refs
        if (dropdown) {
          const selectedElement = dropdown.querySelector('.selected')
          if (selectedElement) {
            // console.log(dropdown.scrollTop, selectedElement.offsetTop)
            dropdown.scrollTop = selectedElement.offsetTop
          }
        }
      })
    },
    tags: function (newVal, oldVal) {
      if (newVal.length !== oldVal.length) {
        Vue.nextTick(() => {
          this.adjustDropdownOffset()
        })
      }
    },
    error: function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.setError(newVal)
      }
    },
    parentError: function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.blur()
      }
    }
  },
  mounted () {
    Vue.nextTick(() => {
      this.adjustDropdownOffset()
    })
  },
  methods: {
    addTag (e) {
      const { tags, minTags, maxTags, minTagLength, maxTagLength, newTag, listOnly } = this
      const nTags = tags.length
      const tLength = e.target.value.length
      const currentItems = [...tags]
      if ([38, 40].includes(e.keyCode)) { // Arrow up or down
        const up = e.keyCode === 38
        e.preventDefault()
        this.selectedItemByMouseover = false
        const filteredAvailableTags = JSON.parse(JSON.stringify(this.filteredAvailableTags))
        const filteredAvailableTagsLength = filteredAvailableTags.length
        const maxIndex = filteredAvailableTagsLength - 1
        if (this.showDropdown && filteredAvailableTagsLength > 0) {
          const itemIndex = filteredAvailableTags.findIndex(tag => tag.id === this.selectedItem)
          if (itemIndex === -1) {
            this.selectedItem = filteredAvailableTags[0].id
          } else {
            if (up) {
              if (itemIndex === 0) {
                this.selectedItem = filteredAvailableTags[maxIndex].id
              } else if (itemIndex > 0) {
                this.selectedItem = filteredAvailableTags[itemIndex - 1].id
              }
            } else {
              if (itemIndex === maxIndex) {
                this.selectedItem = filteredAvailableTags[0].id
              } else if (itemIndex < maxIndex) {
                this.selectedItem = filteredAvailableTags[itemIndex + 1].id
              }
            }
          }
        }
      } else if (e.keyCode === 8) { // Backspace
        if (this.emptied === 1) {
          this.emptied--
        } else if (tLength === 0 && nTags > 0 && this.emptied === 0) {
          if (!currentItems[currentItems.length - 1].locked) {
            currentItems.pop()
            this.$emit('tagsupdated', currentItems)
          }
        }
        this.selectedItem = -1
      } else if (e.keyCode === 13) { // Enter
        if (this.selectedItem > -1) {
          const selectedTag = this.availableTags.find(t => t.id === this.selectedItem)
          if (selectedTag) {
            const index = tags.findIndex(item => item[this.keyValue].toLowerCase() === selectedTag[this.keyValue].toLowerCase())
            if (index > -1) {
              this.repeated = tags[index]
            } else {
              const { dropdown } = this.$refs
              currentItems.push(selectedTag)
              this.$emit('tagsupdated', currentItems)
              this.newTag = ''
              this.emptied = 0
              this.repeated = null
              this.error = null
              this.selectedItem = -1
              dropdown.scrollTo(0, 0)
            }
          }
        } else if (!listOnly && tLength > 0) {
          if (nTags < minTags) {
            this.repeated = null
            this.error = 'It should have at least ' + minTags + ' tags.'
          } else if (nTags >= maxTags) {
            this.repeated = null
            this.error = 'It should have at most ' + maxTags + ' tags.'
          } else if (tLength < minTagLength) {
            this.repeated = null
            this.error = 'A tag should have at least ' + minTagLength + ' characters.'
          } else if (tLength >= maxTagLength) {
            this.repeated = null
            this.error = 'A tag should have at most ' + maxTagLength + ' characters.'
          } else {
            const index = tags.findIndex(item => item[this.keyValue].toLowerCase() === newTag.toLowerCase())
            if (index > -1) {
              this.repeated = tags[index]
            } else {
              const newTagObject = { id: this.newTagId-- }
              newTagObject[this.keyValue] = newTag
              currentItems.push(newTagObject)
              this.$emit('tagsupdated', currentItems)
              this.newTag = ''
              this.emptied = 0
              this.repeated = null
              this.error = null
            }
          }
        }
      } else if (tLength) {
        this.selectedItem = -1
      }
    },
    onItemClick (id) {
      const selectedTag = this.filteredAvailableTags.find(t => t.id === id)
      if (selectedTag) {
        const currentItems = [...this.tags]
        currentItems.push(selectedTag)
        this.$emit('tagsupdated', currentItems)
        this.newTag = ''
        this.emptied = 0
        this.repeated = null
        this.error = null
        this.selectedItem = -1
      }
      this.showDropdown = false
    },
    onItemMouseover (id) {
      this.selectedItemByMouseover = true
      this.selectedItem = id
    },
    onRemoval (tag) {
      const currentItems = [...this.tags]
      const index = currentItems.findIndex(item => item.id === tag.id)
      if (index >= 0 && index < currentItems.length) {
        currentItems.splice(index, 1)
        this.repeated = null
        if (this.newTag.length === 0) {
          this.emptied = 0
        }
        this.$emit('tagsupdated', currentItems)
      }
      this.$refs.newTagInput.focus()
    },
    focus () {
      this.showDropdown = true
    },
    blur () {
      setTimeout(() => {
        this.showDropdown = false
        if (this.required && this.tags.length === 0) {
          this.error = 'This field is required.'
        }
      }, 200)
    },
    adjustDropdownOffset () {
      const { inputTagsContainer, newTagInputContainer, dropdown } = this.$refs
      const max = inputTagsContainer.offsetWidth - newTagInputContainer.offsetLeft
      let newOffsetLeft
      dropdown.style.top = (newTagInputContainer.offsetTop + newTagInputContainer.offsetHeight + 2) + 'px'
      if (max >= dropdown.offsetWidth) {
        newOffsetLeft = newTagInputContainer.offsetLeft
      } else {
        newOffsetLeft = inputTagsContainer.offsetWidth - dropdown.offsetWidth - 1
      }
      if (newOffsetLeft < 10) {
        newOffsetLeft = -1
      }
      dropdown.style.left = newOffsetLeft + 'px'
    }
  }
}
</script>

<style lang="scss" scoped>
  .input_tags {
    margin-bottom: 1.5rem;
    &--error {
      .tag__container{
        border: 1px solid $red;
      }
    }
  }
  .required label::after {
    content: '*';
    color: $red;
    margin-left: 0px;
  }
  .error {
    color: $danger;
  }
  .tag{
    &__container {
      position: relative;
      border: 1px solid $gray-400;
      border-radius: 2px;
      transition: all 1s;
    }
    &__input {
      flex-grow: 100;
      input {
        width: 100%;
        border: 0;
        outline: unset;
        &.error {
          color: $danger;
        }
      }
    }
    &--error {
      border: 1px solid $red;
    }
  }
  .dropdown {
      position: absolute;
      top: 33px;
      max-height: 200px;
      min-width: 75px;
      left: -1px;
      z-index: 1000;
      background-color: white;
      border: 1px solid $gray-400;
      overflow-x: hidden;
      overflow-y: scroll;
    }
  .items {
    & > div.item:not(:first-child) {
      border-top: 1px solid $gray-400;
    }
    &:hover {
      cursor: pointer;
    }
  }
  .item {
    padding: 10px 20px;
    &.selected, &:hover {
      color: $gray-900;
      font-weight: 900;
    }
  }
  .empty {
    padding: 10px 20px;
    color: $gray-900;
    font-weight: 900;
  }
</style>
